(function () {
  /**
   * @deprecated Please use src2/core/models/product/consumable/ConsumableSummarizationList.js
   *
   * @ngdoc factory
   * @name ConsumableSummarizationList
   * @description List of ConsumableSummarization entities
   * As described in https://wiki.corp.adobe.com/display/platformsvcs/Consumables#Consumables-ConsumableSummaryAPI
   */
  angular
    .module('binky.core.product.consumable')
    .factory('ConsumableSummarizationList', getConsumableSummarizationListModel);

  /* @ngInject */
  function getConsumableSummarizationListModel(
    $log,
    $rootScope,
    _,
    binkySrc2,
    CART_EVENT,
    ConsumableSummarization,
    feature,
    jilConsumables,
    List,
    modelCache,
    ORGANIZATION_EVENT,
    OrganizationList,
    PRODUCT_CONFIGURATIONS_EVENT,
    PRODUCT_LIST_EVENT,
    promiseUtils
  ) {
    const {CONSUMABLE_SUMMARIZATION_LIST_CACHE_ID, CONSUMABLE_SUMMARIZATION_SUMMARIZE_BY} =
      binkySrc2.models.product.consumable;

    class ConsumableSummarizationList extends List {
      /**
       * @description Creates a new ConsumableSummarizationList for use.
       *
       * @param {Object} [options] - options to configure the consumable summarization
       * @param {String} [options.contract_id] (optional) when specified, filter the usage to only include usage associated with the requested contract id
       * @param {String} [options.delegation_type] (optional) when specified, filter the usage to only include usage associated with the specific delegation_type.
       *     Remove with temp_sls_2_5
       * @param {String} [options.fulfillment_id] (optional) when specified, filter the usage to only include usage associated with the requested fulfillment id or ids
       * @param {String} [options.group_id] (optional) when specified, filter the usage to only include usage associated with the specific delegation group id or ids
       * @param {Boolean} [options.include_depleted] (optional) when present, include consumable usage whose remaining quantity is zero (defaults to true)
       * @param {Boolean} [options.include_expired] (optional) when present, for consumables that expire, include expired consumables in summary and usage details (defaults to false)
       * @param {Boolean} [options.include_usage] (optional) whether or not to include individual Usage details along with the summary information (defaults to true)
       * @param {Number} [options.include_usage_limit] (optional) max number of Usage details to include; server may return fewer (defaults to 5)
       * @param {String} [options.license_id] (optional) when specified, filter the usage to only include usage associated with the specific license id or ids
       * @param {String} options.organization_id organization id of org this summarization pertains to
       * @param {String} [options.sort_by] (optional) field name, i.e. "expiration_date"
       * @param {String} [options.summarize_by] (optional) dimension being summarized, such as "license_id" or "order_id"; defaults to organization_id
       */
      constructor(options = {}) {
        super({
          cacheType: CONSUMABLE_SUMMARIZATION_LIST_CACHE_ID,
          itemClassRef: ConsumableSummarization,
          resource: jilConsumables.summary,
        });
        initModel(this, options);

        this.deregisterOnProfileAddedHandler = $rootScope.$on(
          PRODUCT_CONFIGURATIONS_EVENT.CREATE,
          this.refresh.bind(this)
        );
      }

      /**
       * @description Method to free up any resources that need to be explicitly
       *   removed to prevent memory leaks (i.e. - listeners/handlers, etc).
       */
      destroy() {
        _.invoke(this, 'deregisterOnProfileAddedHandler');
      }

      /**
       * @description Find any Consumable objects that are for the specified fulfillable item code
       *
       * @param {String} fiCode the fulfillable item code to find summaries for
       * @param {Object} options additional options for finding the Consumables
       * @param {String} [options.contract_id] find only Consumables for the specified contract id
       *
       * @returns {Array} List of Consumables that are for the fulfillable item code
       */
      getConsumablesForFulfillableItemCode(fiCode, options) {
        if (_.has(options, 'contract_id')) {
          // Only search for matching Consumables in the summarization for the desired contract
          return _(this.items)
            .filter({contractId: options.contract_id})
            .flatMap((item) => item.getConsumablesForFulfillableItemCode(fiCode, options))
            .value();
        }

        return _.flatMap(this.items, (item) =>
          item.getConsumablesForFulfillableItemCode(fiCode, options)
        );
      }

      /**
       * @description Find any Consumable objects that are for the specified summary ID
       *
       * @param {String} id the ConsumableSummary's id ("value" of dimension this is a summary of) to find consumables for.
       * @param {Object} [options] additional options for finding the Consumables
       * @param {String} [options.summarize_by] check for the dimension being summarized before returning the Consumables
       *
       * @returns {Array} List of Consumables that are for the ID
       */
      getConsumablesForSummaryId(id, options = {}) {
        const summaries = this.getSummariesForId(id, options);
        return _.flatMap(summaries, (summary) => _.get(summary, 'consumableList.items'));
      }

      /**
       * @description Find any ConsumableSummary objects that are for the specified ID
       *
       * @param {String} id the ID to find summaries for
       * @param {Object} [options] additional options for finding the Summaries
       * @param {String} [options.summarize_by] check for the dimension being summarized before returning the Summaries
       *
       * @returns {Array} List of Summaries that are for the ID
       */
      getSummariesForId(id, options = {}) {
        if (options.summarize_by && this.summarize_by !== options.summarize_by) {
          $log.warn(
            `The field summarize_by = ${options.summarize_by} does not match with the current dimension being summarized ${this.summarize_by}. Thus, no summary for the ID will be found.`
          );
          return [];
        }
        return _.flatMap(this.items, (item) => item.getSummariesForId(id));
      }

      key() {
        return super.key(getQueryParams(this, true));
      }

      /**
       * @description Method to fetch an array of ConsumableSummarizations
       *
       * @returns {Promise} Resolves with refreshed model if successful, else
       *                    rejects with error message
       */
      refresh() {
        const params = getQueryParams(this);
        const promise = super.refresh(_.assign({orgId: this.organization_id}, params));
        promise
          .then(() => {
            _.forEach(this.items, (item) => item.setParentSummarizationList(this));
          })
          .catch(_.noop);
        return promise;
      }

      /**
       * @description Method to fetch the ConsumableSummarizationList.
       * @param {Object} [options] - see constructor for options properties
       * @returns {ConsumableSummarizationList} the fetched ConsumableSummarizationList.
       */
      static get(options) {
        let model = new ConsumableSummarizationList(options);

        const cachedModel = modelCache.get(CONSUMABLE_SUMMARIZATION_LIST_CACHE_ID, model.key());
        if (cachedModel) {
          model = cachedModel;
        } else {
          model.refresh();
        }
        return model;
      }
    }

    class ConsumableSummarizationList2 extends binkySrc2.models.product.consumable
      .ConsumableSummarizationList {
      /**
       * @description Creates a new ConsumableSummarizationList instance.
       * @param {Object} [options] - see ConsumableSummarizationList.js
       */
      constructor(options) {
        super(options);

        this.deregisterOnProfileAddedHandler = $rootScope.$on(
          PRODUCT_CONFIGURATIONS_EVENT.CREATE,
          this.refresh.bind(this)
        );
      }

      /**
       * @description Method to free up any resources that need to be explicitly
       *     removed to prevent memory leaks (listeners, handlers, etc.)
       */
      destroy() {
        _.invoke(this, 'deregisterOnProfileAddedHandler');
      }

      /**
       * @description Method to fetch an array of ConsumableSummarizations
       *
       * @returns {Promise} Resolves with refreshed model if successful, else
       *     rejects with error message
       */
      refresh() {
        this.$resolved = false;
        this.$promise = promiseUtils.toAngularPromise(super.refresh()).finally(() => {
          this.$resolved = true;
        });
        return this.$promise;
      }

      /**
       * @description Method to fetch the ConsumableSummarization. See
       *     ConsumableSummarizationList.js for params.
       * @param {Object} [options] - see ConsumableSummarizationList.js
       * @returns {ConsumableSummarizationList} the fetched ConsumableSummarizationList.
       */
      static get(options) {
        const model = new ConsumableSummarizationList2(options);
        model.$resolved = false;
        model.$promise = promiseUtils
          .toAngularPromise(super.get(options))
          .then((response) => {
            _.assign(model, response);
            return model;
          })
          .finally(() => {
            model.$resolved = true;
          });
        return model;
      }
    }

    //////////////

    function initModel(model, options) {
      const defaults = {
        include_depleted: true,
        include_expired: false,
        include_usage: true,
        include_usage_limit: 5,
        items: [],
        summarize_by: CONSUMABLE_SUMMARIZATION_SUMMARIZE_BY.ORGANIZATION_ID,
      };
      // First we assign the model fields
      _.defaults(model, getQueryParams(options, true), defaults);
      // Only fetch the org id from OrganizationList if it wasn't provided. Not all users
      // of binky set an activeOrg on OrganizationList, so we want to be able to specify
      // it in the options and NOT call for it in that case.
      if (!options.organization_id) {
        const organizationList = OrganizationList.get();
        model.organization_id = _.get(organizationList, 'activeOrg.id');
      }
      if (options.items) {
        model.items = _.map(options.items, (item) =>
          ConsumableSummarization.apiResponseTransformer(item)
        );
      }
    }

    function getQueryParams(model, includeOrganizationId) {
      // return the parameters we have that correspond to API query params.
      const propertyArray = [
        'contract_id',
        'delegation_type', // Remove with temp_sls_2_5
        'fulfillment_id',
        'group_id',
        'include_depleted',
        'include_expired',
        'include_usage',
        'include_usage_limit',
        'license_id',
        'order_id',
        'sort_by',
        'summarize_by',
      ];
      if (includeOrganizationId) {
        propertyArray.push('organization_id');
      }
      return _.pick(model, propertyArray);
    }

    // Making the cache size greater than 1 to allow for specialized summarizations per order, etc.
    modelCache.register(CONSUMABLE_SUMMARIZATION_LIST_CACHE_ID, 3);
    modelCache.removeAllOnEvent(CONSUMABLE_SUMMARIZATION_LIST_CACHE_ID, [
      CART_EVENT.SUBMIT,
      ORGANIZATION_EVENT.UPDATE,
      PRODUCT_LIST_EVENT.REFRESH,
    ]);

    return feature.isEnabled('temp_port_consumable_summarization_list')
      ? ConsumableSummarizationList2
      : ConsumableSummarizationList;
  }
})();
